import { Box, Divider, Grid, IconButton, Link, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { AddCircleOutline, AssignmentOutlined, BusinessOutlined, Check, Close, ComputerOutlined, DesignServicesOutlined, HelpOutlineOutlined, KeyboardArrowLeft, KeyboardArrowRight, Phone, RateReviewOutlined, ScheduleOutlined, SupportAgentOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { getSiteHoursReport, getSiteSubmissions, getSiteSubscription } from '../../../actions/internal/SiteActions';
import { getSiteById, getSiteSubmissionsById, getSiteSubscriptionById } from '../../../selectors/SiteSelectors';
import Colors from "../../../constants/Colors";
import { formatDateTimeToFancyString, formatDateToFancyString, getMonthName } from "../../../utilities/DateUtilities";
import { withRouter } from 'react-router';
import { observer } from 'mobx-react-lite';
import StyledSubHeader from '../../common/StyledSubHeader';
import StyledProgressCircle from '../../common/StyledProgressCircle';
import { ContainedButton, TextButton, TooltipIconButton } from '../../common/StyledButtons';
import { CARD_CAPTION_STYLE, CARD_HEADER_STYLE } from '../../../constants/Styles';
import { getUserId } from '../../../selectors/UserSelectors';
import StyledChip from '../../common/StyledChip';
import StyledPage from '../../common/StyledPage';
import StyledSection from '../../common/StyledSection';
import StyledStatusIcon from '../../common/StyledStatusIcon';
import { SUBMISSION_TYPES_HUMAN_MAP } from '../../../constants/Statuses';
import ViewRequestModal from '../modals/ViewRequestModal';
import AddRequestModal from '../modals/AddRequestModal';

const ViewSiteHoursComponent = ({ match, history }) => {
    const date = new Date();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [ isLoading, setIsLoading ] = useState(true);
    const [ isLoadingDate, setIsLoadingDate ] = useState(false);
    const [ currentDate, setCurrentDate ] = useState(date);
    const [ hoursReport, setHoursReport ] = useState();

    const [ isLoadingRequests, setIsLoadingRequests ] = useState(false);
    const [ requestInView, setRequestInView ] = useState();
    const [ showAddRequestModal, setShowAddRequestModal ] = useState(false);

    const siteId = match.params.site_id
    const subscription = getSiteSubscriptionById(siteId);
    const submissions = getSiteSubmissionsById(siteId);

    useEffect(() => {
        loadData().then();
        loadRequests().then();
        loadHoursReport().then();
    }, [])

    const loadData = async () => {
        try {
            const userId = getUserId();
            await getSiteSubscription(userId, siteId)
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    }

    const loadRequests = async () => {
        try {
            setIsLoadingRequests(true);
            await getSiteSubmissions(siteId)
        } catch (ex) {
            showNotificationBar(NOTIFICATION_TYPE.ERROR, 'An error has occurred while retrieving your requests. Please try again later');
        } finally {
            setIsLoadingRequests(false);
        }
    }

    const loadHoursReport = async () => {
        try {
            setIsLoadingDate(true);
            const report = await getSiteHoursReport(siteId, 0);
            setHoursReport(report)
        } catch(err) {
            console.error(err);
            showNotificationBar('error', 'An error has occurred while fetching hours. Please try again later.')
        } finally {
            setIsLoadingDate(false)
        }
    }

    const hasSupportPlan = () => {
        if (!subscription || !subscription.site_package || !subscription.site_package.support_product.name) return false;
        return subscription.site_package.support_product.is_active;
    }

    const getSupportPlanName = () => {
        if (!subscription || !subscription.site_package) return false;
        return subscription.site_package.support_product.name;
    }

    const getSupportPlanDescription = () => {
        if (!subscription || !subscription.site_package) return false;
        return subscription.site_package.support_product.description;
    }

    const handleExploreCompletedSupport = () => history.push(`/dashboard/site/${siteId}/support/completed`)

    const renderLoadingContent = () => (
        <StyledProgressCircle />
    )

    const renderSupportPlanDetails = () => {
        if (!hasSupportPlan()) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                    <Box>
                        <h6 style={{ textAlign: 'left', padding: 0, fontSize: '24px' }}>No Active Support Plan</h6>
                        <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '600px' }}>This account doesn’t currently have a BCK Support Plan. While one-time support is available, having a plan ensures faster response times, discounted rates, and priority service. Let us know if you'd like to explore your options.</p>
                    </Box>
                </Box>
            )
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                <Box>
                    <h6 style={{ textAlign: 'left', padding: 0 }}>{getSupportPlanName()}</h6>
                    <p style={{ marginTop: '8px', textAlign: 'left', maxWidth: '600px' }}>{getSupportPlanDescription()}</p>
                </Box>
            </Box>
        )
    }

    const renderPerksSection = () => {
        if (!hasSupportPlan()) return [];
        return subscription.site_package.support_product.perks && subscription.site_package.support_product.perks.map(perk => (
            <Box key={perk.name} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginRight: '18px', marginBottom: '10px' }}>
                    <Check sx={{ color: Colors.primaryColor, fontSize: '24px' }} />
                </Box>
                <Box>
                    <h6 style={{ textAlign: 'left', padding: 0, fontSize: '20px', fontWeight: 'normal' }}>{perk.name}</h6>
                </Box>
            </Box>
        ))
    }

    const renderOverviewSection = () => (
        <StyledSection
            title="Support Plan"
            rightAdornment={ <StyledChip
                text={hasSupportPlan() ? 'Active' : 'Inactive'}
                sx={{ backgroundColor: hasSupportPlan() ? Colors.primaryColor : Colors.greyColor }}
                />}
        >
            {renderSupportPlanDetails()}
            {renderPerksSection()}
        </StyledSection>
    )

    const prepareTools = () => {
        const tools = []

        
        tools.push({
            name: "Have Us Edit Your Website",
            icon: <DesignServicesOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
            description: 'Need to update text, images, or content? Send your request here.',
            onClick: () => setShowAddRequestModal(true),
            buttonText: "Request"
        })

        tools.push({
            name: "Schedule an Appointment",
            icon: <ScheduleOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
            description: 'Book a time to meet and discuss your project, updates, or strategy.',
            link: "https://consultwithkyle.com/appointments",
            onClick: () => window.open('https://consultwithkyle.com/appointments', '_blank'),
            buttonText: "Book Now"
        })

        tools.push({
            name: "View my Rate",
            icon: <RateReviewOutlined sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
            description: 'Check your hourly rate for support from BCK here.',
            onClick: () => history.push(`/dashboard/site/${siteId}/account/billing`),
            buttonText: "Request"
        })

        return tools;
    }

    const renderToolCards = () =>
        prepareTools().map((tool) => (
        <Grid item xs={12} key={tool.name}>
            <Paper
            variant="outlined"
            onClick={tool.onClick}
            sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '100%',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                boxSizing: 'border-box',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    transform: 'scale(1.02)',
                  },
            }}
            >
            {/* Left: Icon + Text */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                <Box>{tool.icon}</Box>
                <Box>
                <div style={CARD_HEADER_STYLE}>{tool.name}</div>
                <p style={{ margin: 0 }}>{tool.description}</p>
                </Box>
            </Box>
            
            </Paper>
        </Grid>
    ));

    const renderActionsSection = () => (
        <StyledSection
            title="Actions"
        >  
            <Grid container item xs={12} spacing={1}>
                {renderToolCards()}
            </Grid>
        </StyledSection>
    )

    const renderSubmissionTitle = (submission) => {
        if(!submission) return 'N/A';
        const type = (SUBMISSION_TYPES_HUMAN_MAP[submission.submission_type] || submission.submission_type);
        const title = submission.submission_fields.title ? submission.submission_fields.title : 'Untitled Request';
        return <span>{type}: {title}</span>
    }

    const renderTooltip = () => (
        <Tooltip
            title={
            <>
                This section shows all active requests open for your business.
                Requests are a way to submit necessary changes or ask for help with problems you might be facing.
            </>
            }
            arrow
            placement="top"
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>Active Requests</span>
            <HelpOutlineOutlined
                fontSize="small"
                sx={{ marginLeft: '4px', cursor: 'pointer' }}
            />
            </div>
        </Tooltip>
    )

    const renderOpenNewButton = () => (
        <TextButton
          onClick={() => setShowAddRequestModal(true)}
          sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
        >
          <AddCircleOutline fontSize="small" sx={{ marginRight: '4px' }} />
          Open New Request
        </TextButton>
    );

    const renderRequestsSection = () => (
        <StyledSection
            title={<>{renderTooltip()}</>}
            rightAdornment={renderOpenNewButton()}
        >
            {isLoadingRequests ? 
                <StyledProgressCircle />
                :
                <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {submissions && submissions.map((submission, index) => (
                            <ListItem
                            dense
                            key={submission.submission_id}
                            sx={{
                                backgroundColor: '#fff',
                                marginBottom: '5px',
                                paddingRight: '55px',
                                borderBottom: index !== submissions.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                            >
                            <ListItemAvatar>
                                <StyledStatusIcon status={submission.submission_status} />
                            </ListItemAvatar>

                            <ListItemText
                                primary={renderSubmissionTitle(submission)}
                                secondary={`Received on ${formatDateToFancyString(submission.submission_received_epoch)}`}
                            />

                            <ListItemSecondaryAction sx={{ marginLeft: "10px" }}>
                                <TooltipIconButton
                                text="View Details"
                                icon={
                                    <AssignmentOutlined
                                        sx={{ color: Colors.primaryColor }}
                                        onClick={() => setRequestInView(submission)}
                                    />
                                }
                                />
                            </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        
                        {(!submissions || submissions.length === 0) && <p>No active requests.</p>}
                    </List>
                </Paper>
            }
        </StyledSection>
    )

    const renderCompletedRequestsSection = () => {
        let totalHours = "0.00";
        let cycleEndDate = "-";
        let completedWithTime = 0;
        let trackedRequests = [];
    
        if (hoursReport) {
            if (hoursReport.time_utilized && hoursReport.time_utilized.total_time_hours) {
                totalHours = hoursReport.time_utilized.total_time_hours;
            }
    
            if (hoursReport.time_period && hoursReport.time_period.end) {
                const endDate = new Date(hoursReport.time_period.end);
                cycleEndDate = endDate.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
    
            if (Array.isArray(hoursReport.tracked_completed_requests)) {
                trackedRequests = hoursReport.tracked_completed_requests;
                completedWithTime = trackedRequests.filter((r) => {
                    return r.submission_time_utilized &&
                        r.submission_time_utilized.total_time_seconds > 0;
                }).length;
            }
        }
    
        return (
            <StyledSection title="Support Completed">
                {isLoadingDate ? (
                    <StyledProgressCircle />
                ) : (
                    <>
                        {/* Info Grid */}
                        <Grid container spacing={2} sx={{ marginBottom: '12px' }}>
                            {[{
                                label: 'Total Hours Completed',
                                value: `${totalHours} hrs`
                            }, {
                                label: 'Cycle End Date',
                                value: cycleEndDate
                            }, {
                                label: 'Requests Tracked',
                                value: completedWithTime
                            }].map((item, idx) => (
                                <Grid item xs={12} sm={4} key={idx}>
                                    <Box
                                        sx={{
                                            padding: '12px',
                                            minHeight: isMobile ? 'auto' : '100px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <h4 style={{ fontSize: '14px', marginBottom: '4px', marginTop: 0 }}>{item.label}</h4>
                                        <h3 style={{ fontSize: '24px', margin: 0 }}>{item.value}</h3>
                                    </Box>
                                    {(isMobile && idx !== 2) && <Divider /> }
                                </Grid>
                            ))}
                        </Grid>
    
                        {trackedRequests && trackedRequests.length > 0 &&
                            <Paper variant="outlined" sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                                <List style={{ flex: 1 }}>
                                    {trackedRequests.length > 0 ? (
                                        trackedRequests.map((submission, index) => (
                                            <ListItem
                                                dense
                                                key={submission.submission_id}
                                                sx={{
                                                    backgroundColor: '#fff',
                                                    marginBottom: '5px',
                                                    borderBottom:
                                                        index !== trackedRequests.length - 1
                                                            ? '1px solid #e0e0e0'
                                                            : 'none',
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <StyledStatusIcon status={submission.submission_status} />
                                                </ListItemAvatar>

                                                <ListItemText
                                                    primary={renderSubmissionTitle(submission)}
                                                    secondary={`Closed on ${formatDateToFancyString(submission.submission_closed_epoch)}`}
                                                />

                                                <ListItemSecondaryAction>
                                                    <TooltipIconButton
                                                        text="View Details"
                                                        icon={
                                                            <AssignmentOutlined
                                                                sx={{ color: Colors.primaryColor }}
                                                                onClick={() =>
                                                                    setRequestInView({
                                                                        ...submission,
                                                                        submission_hours: submission.submission_time_entries,
                                                                    })
                                                                }
                                                            />
                                                        }
                                                    />
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))
                                    ) : (
                                        <p>No completed requests.</p>
                                    )}
                                </List>
                            </Paper>
                        }
                        
                    </>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', marginTop: '32px', position: 'relative', zIndex: 15}}>
                    <ContainedButton
                        sx={{ padding: '8px 16px 8px 16px' }}
                        onClick={handleExploreCompletedSupport}
                    >View More</ContainedButton>
                </Box>
            </StyledSection>
        );
    };
    
    
    
    
    

    return (
        <>
            <StyledPage>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <StyledSubHeader title="Support" />
                    </Grid>

                    <Grid item xs={12}>
                    {isLoading ? (
                        renderLoadingContent()
                    ) : (
                        <>
                            {renderOverviewSection()}
                            {renderRequestsSection()}
                            {renderCompletedRequestsSection()}
                            {renderActionsSection()}
                        </>
                    )}
                    </Grid>
                </Grid>
            </StyledPage>
            <ViewRequestModal
                open={!!requestInView}
                onClose={() => setRequestInView()}
                request={requestInView}
            />
            <AddRequestModal
                open={showAddRequestModal}
                onClose={() => setShowAddRequestModal(false)}
                siteId={siteId}
            />
      </>
    )


}

export default observer(withRouter(ViewSiteHoursComponent));
